import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  User, Users, CreditCard, Settings as SettingsIcon, Building, Share2,
  Scissors, UserCog, Globe, Sparkles
} from 'lucide-react';

const TABS = [
  { value: 'profile', label: 'Profilo', icon: User },
  { value: 'company', label: 'Azienda', icon: Building },
  { value: 'social', label: 'Social', icon: Share2 },
  { value: 'brand', label: 'Brand AI', icon: Sparkles },
  { value: 'services', label: 'Servizi', icon: Scissors },
  { value: 'staff', label: 'Staff', icon: UserCog },
  { value: 'team', label: 'Team', icon: Users },
  { value: 'subscription', label: 'Abbonamento', icon: CreditCard },
  { value: 'preferences', label: 'Preferenze', icon: SettingsIcon },
  { value: 'platform', label: 'Piattaforma', icon: Globe, superadminOnly: true },
];

import ProfileTab from '../components/settings/ProfileTab';
import CompanyTab from '../components/settings/CompanyTab';
import SocialTab from '../components/settings/SocialTab';
import ServicesTab from '../components/settings/ServicesTab';
import StaffTab from '../components/settings/StaffTab';
import TeamTab from '../components/settings/TeamTab';
import SubscriptionTab from '../components/settings/SubscriptionTab';
import PreferencesTab from '../components/settings/PreferencesTab';
import PlatformTab from '../components/settings/PlatformTab';
import BrandBriefTab from '../components/settings/BrandBriefTab';

const SettingsPage = () => {
  const { user, API, getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [socialConnections, setSocialConnections] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeTab = searchParams.get('tab') || 'profile';

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, staffRes, connectionsRes, companyRes] = await Promise.all([
        axios.get(`${API}/services`, getAuthHeaders()),
        axios.get(`${API}/staff`, getAuthHeaders()),
        axios.get(`${API}/social/connections`, getAuthHeaders()),
        axios.get(`${API}/company-profile`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setServices(servicesRes.data);
      setStaffList(staffRes.data);
      setSocialConnections(connectionsRes.data);
      if (companyRes.data) setCompanyData(companyRes.data);

      if (user?.role === 'admin') {
        const teamRes = await axios.get(`${API}/team`, getAuthHeaders());
        setTeam(teamRes.data);
      }

      if (user?.role === 'superadmin') {
        try {
          const brandingRes = await axios.get(`${API}/platform-branding`, getAuthHeaders());
          setPlatformData(brandingRes.data);
        } catch (e) {
          // Platform branding non configurato
        }
      }
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit">Impostazioni</h1>
        <p className="text-muted-foreground mt-1">Gestisci il tuo account e le preferenze</p>
      </div>

      <Tabs value={activeTab}
        onValueChange={(v) => setSearchParams(v === 'profile' ? {} : { tab: v }, { replace: true })}
        className="space-y-6">
        {/* Mobile: elenco a tendina, le tab in fila sono illeggibili su schermi stretti */}
        <div className="lg:hidden">
          <Select value={activeTab}
            onValueChange={(v) => setSearchParams(v === 'profile' ? {} : { tab: v }, { replace: true })}>
            <SelectTrigger className="w-full min-h-[48px]" data-testid="settings-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TABS.filter(t => !t.superadminOnly || user?.role === 'superadmin').map(t => (
                <SelectItem key={t.value} value={t.value} data-testid={`tab-option-${t.value}`}>
                  <span className="flex items-center gap-2">
                    <t.icon className="w-4 h-4 text-muted-foreground" />{t.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden lg:inline-flex w-auto justify-start gap-1 h-auto p-1" data-testid="settings-tabs">
          {TABS.filter(t => !t.superadminOnly || user?.role === 'superadmin').map(t => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
              <t.icon className="w-4 h-4 mr-2" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <ProfileTab user={user} />
        </TabsContent>

        <TabsContent value="company" className="space-y-6">
          <CompanyTab initialData={companyData} />
        </TabsContent>

        <TabsContent value="social" className="space-y-6">
          <SocialTab socialConnections={socialConnections} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="brand" className="space-y-6">
          <BrandBriefTab />
        </TabsContent>

        <TabsContent value="services" className="space-y-6">
          <ServicesTab services={services} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="staff" className="space-y-6">
          <StaffTab staffList={staffList} services={services} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="team" className="space-y-6">
          <TeamTab user={user} team={team} />
        </TabsContent>

        <TabsContent value="subscription" className="space-y-6">
          <SubscriptionTab />
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          <PreferencesTab />
        </TabsContent>

        {user?.role === 'superadmin' && (
          <TabsContent value="platform" className="space-y-6">
            <PlatformTab initialData={platformData} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
